import { useEffect } from 'react';
import { useChat } from '../../hooks/useChat';
import { useAuthContext } from '../../contexts/AuthContext';
import { ChatMessage } from './ChatMessage';
import { SystemMessage } from './SystemMessage';
import { MessageInput } from './MessageInput';

interface ChatSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  leagueId: string;
  leagueName: string;
}

export function ChatSlideout({ isOpen, onClose, leagueId, leagueName }: ChatSlideoutProps) {
  const { profile } = useAuthContext();

  // Get display name: use displayName if set, otherwise extract from email
  const displayName =
    profile?.displayName || (profile?.email ? profile.email.split('@')[0] : undefined);

  const {
    messages,
    loading,
    error,
    sending,
    sendMessage,
    markAsRead,
    messagesEndRef,
    scrollToBottom,
  } = useChat(leagueId, displayName);

  // Mark as read when opened
  useEffect(() => {
    if (isOpen) {
      markAsRead();
      setTimeout(scrollToBottom, 300);
    }
  }, [isOpen, markAsRead, scrollToBottom]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slideout - Account for bottom nav (64px) */}
      <div
        className={`fixed top-0 right-0 bottom-16 w-full max-w-md bg-[#1e1e1e] z-50 shadow-2xl transform transition-transform duration-300 flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="bg-[#2c2c2c] border-b border-gray-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-200 transition-colors"
              aria-label="Close chat"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div>
              <h2 className="text-lg font-bold text-white">League Chat</h2>
              <p className="text-xs text-gray-400 truncate">{leagueName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 transition-colors text-xl"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#1e1e1e]">
          {loading && messages.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-400">Loading messages...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-full">
              <div className="text-center text-red-400">
                <p className="font-medium">Failed to load messages</p>
                <p className="text-sm mt-1">{error}</p>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <div className="text-center text-gray-500">
                <p className="text-4xl mb-3">💬</p>
                <p className="mt-2 text-sm text-gray-400">No messages yet</p>
                <p className="text-xs mt-1 text-gray-500">Be the first to say something!</p>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) =>
                message.type === 'SYSTEM' ? (
                  <SystemMessage key={message.id} message={message} />
                ) : (
                  <ChatMessage key={message.id} message={message} />
                )
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <MessageInput onSend={sendMessage} disabled={sending} />
      </div>
    </>
  );
}
